import { Trans, useTranslation } from 'react-i18next';
import { ButtonAsLink, OTPInput, ProColors } from '@treatwell/treatwell-aura';
import { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { apiContext, v1VerificationSendVerificationCode } from '../api-client';
import { useFormContext } from '../state';
import { useCaptchaContext } from '../features/captcha-context';
import { Wave1, Wave5 } from '../aphrodite';
import { Form, Title } from './templates';
import { useFormErrors } from './input/helpers/use-form-errors';

export const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 15_000;

export function StepEmailVerification() {
  const { t } = useTranslation('stepEmailVerification');
  const [disabled, setDisabled] = useState(false);
  const {
    props: { subText },
    clear,
  } = useFormErrors('verificationCode');

  const { executeCaptcha } = useCaptchaContext();
  const { data, setData } = useFormContext();

  const onValueChange = useCallback(
    async (value: string) => {
      clear();
      setData(current => ({ ...current, verificationCode: value }));
    },
    [setData, clear],
  );

  const onSend = useCallback(async () => {
    if (disabled) {
      return;
    }

    const token = await executeCaptcha('self_signup/send_verification_code');
    await v1VerificationSendVerificationCode(apiContext, {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      businessName: data.businessName,
      language: navigator.language || data.address.countryCode,
      token,
    });
    setDisabled(true);
    setTimeout(() => setDisabled(false), RESEND_COOLDOWN);
  }, [data, executeCaptcha, setDisabled, disabled]);

  // On error, clear the input
  useEffect(() => {
    if (subText) {
      setData(current => ({ ...current, verificationCode: '' }));
    }
  }, [subText, setData]);

  return (
    <Form>
      <Title
        css={css({ textAlign: 'center', maxWidth: 500, alignSelf: 'center' })}
        text={
          <Trans
            t={t}
            i18nKey="title"
            transKeepBasicHtmlNodesFor={['br']}
            components={[<span css={css({ color: ProColors.teal[600] })} />]}
          />
        }
      />
      <div css={css({ width: 64, margin: 'auto' })}>
        <Wave5 color={ProColors.pink[600]} strokeWidth={1.4} />
      </div>
      <div
        css={css({
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          alignItems: 'center',
          position: 'relative',
        })}
      >
        <OTPInput
          length={CODE_LENGTH}
          value={data.verificationCode || ''}
          onValueChange={onValueChange}
          subTextFlavor="error"
          subText={subText}
        />
      </div>
      <div
        className="aura--copy-normal"
        css={css({
          color: ProColors.navy[800],
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          marginBottom: 12,
          fontWeight: 400,
        })}
      >
        <span> {t('subTitle1')}</span>
        <span css={css({ display: 'flex', gap: 8, alignSelf: 'center' })}>
          <span> {t('subTitle2')}</span>
          <ButtonAsLink onClick={onSend} disabled={disabled}>
            {t('resend')}
          </ButtonAsLink>
        </span>
      </div>

      <div css={css({ position: 'relative' })}>
        <div
          css={css({
            position: 'absolute',
            right: 137,
            top: 15,
            transform: 'scaleY(-1) rotate(-149deg)',
            width: 240,
            height: 64,
          })}
        >
          <Wave1 color={ProColors.yellow[600]} />
        </div>
      </div>
    </Form>
  );
}
